<template>
  <div id="contenedor-nuevo-pago">
    <v-form
      v-model="formularioValido">
      <v-row
        justify="space-between">
        <date-picker
          cols="12"
          md="6"
          v-model="pago.fecha"
          label="Fecha"
          :rules="[mixReglas.requerido]">
        </date-picker>
        <v-col
          cols="12"
          md="6">
          <custom-text-field
            v-model="pago.referencia"
            label="Referencia"
            :rules="[mixReglas.requerido]">
          </custom-text-field>
        </v-col>
        <v-col
          cols="12">
          <v-divider></v-divider>
        </v-col>
        <custom-auto-complete
          v-model="pago.idContratista"
          cols="12"
          md="12"
          label="Seleccionar contratista"
          :items="mixGetContratitasSolicitudes"
          item-text="nombreRazonSocial"
          item-value="id">
        </custom-auto-complete>
        <custom-auto-complete
          v-model="idContrato"
          cols="12"
          md="12"
          label="Filtrar por contrato"
          :items="listadoContratos"
          item-text="numero"
          item-value="id">
        </custom-auto-complete>
        <v-col
          cols="12"
          style="height: 230px;">
          <h3>Listado de solicitudes
            <v-chip
              small>
              {{ solicitudesPorContratista.length || 0 }}
            </v-chip>
          </h3>
          <template
            v-if="solicitudesPorContratista.length <= 0">
            <div class="grey lighten-4" style="height: 200px">
              <div class="text-center pt-4">
                <span class="font-weight-medium">NOTA:</span> Debe seleccionar un contratista.
              </div>
            </div>
          </template>
          <template
            v-else>
            <v-simple-table
              fixed-header
              height="200px">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th></th>
                    <th>Tipo</th>
                    <th>Contrato</th>
                    <th>Num Factura</th>
                    <th>Fecha Factura</th>
                    <th class="text-right">
                      Monto
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="(detalle) in solicitudesPorContratista">
                    <template>
                      <tr
                        :key="detalle.id">
                        <td class="text-center">
                          <v-checkbox
                            v-model="pago.detalles"
                            :value="detalle">
                          </v-checkbox>
                        </td>
                        <td>
                          <v-chip
                            :color="detalle.color"
                            dark>
                            {{ detalle.tipo }}
                          </v-chip>
                        </td>
                        <td>
                          {{ detalle.contrato.numero }}
                        </td>
                        <td>
                          {{ detalle.numeroFactura }}
                        </td>
                        <td>
                          {{ new Date(`${detalle.fechaFactura} 00:00:0`).toLocaleDateString() }}
                        </td>
                        <td class="text-right">
                          <v-chip>
                            {{ detalle.importe | currency }}
                          </v-chip>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-col>
        <v-col
          md="3">
        </v-col>
        <v-col
          cols="12"
          md="3"
          align-self="end">
          <custom-text-field
            v-model.number="pago.importe"
            label="Monto a pagar"
            es-dinero
            readonly
            prefix="$"
            :rules="[mixReglas.requerido]">
          </custom-text-field>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import CustomTextField from '@/components/inputs/sinColumnas/TextField.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import CustomAutoComplete from '@/components/inputs/Autocomplete.vue';
import mixPagos from '../mixPagos';

export default {
  name: 'NuevoPago',
  mixins: [mixPagos],
  components: {
    CustomTextField,
    DatePicker,
    CustomAutoComplete,
  },
  data() {
    return {
      formularioValido: false,
      enviandoFormulario: false,
      idContrato: null,
      pago: {
        idContratista: null,
        fecha: '',
        importe: 0,
        referencia: '',
        detalles: [],
      },
    };
  },
  methods: {
    async guardar() {
      // Formatear los detalles del pago
      const detallesFormateados = this.pago.detalles.map((detalle) => ({
        idPagosSolicitudesDetalles: detalle.id,
        importe: detalle.importe,
        esNuevo: false,
      }));

      // set los detalles del pago
      this.pago.detalles = detallesFormateados;

      // Guardar pago
      await this.mixGuardarPago();
    },
    cerrar() {
      this.pago.idContratista = '';
      this.pago.fecha = '';
      this.pago.referencia = '';
      this.pago.importe = 0;
      this.pago.detalles = [];

      this.$emit('cerrar');
    },
  },
  computed: {
    listadoContratos() {
      return this.mixGetContratosSolicitudes.filter((contrato) => {
        if (!this.pago.idContratista) return true;

        return contrato.contratista.id === this.pago.idContratista;
      });
    },
    solicitudesPorContratista() {
      return this.mixPagosSolicitudes
        .filter(({ contrato }) => contrato.contratista.id === this.pago.idContratista)
        .sort((a, b) => new Date(b.createAt) - new Date(a.createAt))
        .map(({ detalles }) => detalles)
        .flat()
        .filter(({ id }) => this.mixSolicitudesPagadas.includes(id) === false)
        .map((detalle) => {
          let contrato = null;
          let tipo = null;
          let color = null;

          if (detalle.anticipo) {
            tipo = 'Anticipo';
            color = 'green darken-1';
            contrato = detalle.anticipo.contrato;
          }

          if (detalle.estimacion) {
            tipo = 'Estimación';
            color = 'light-blue darken-1';
            contrato = detalle.estimacion.contrato;
          }

          if (detalle.escalatoria) {
            tipo = 'Escalatoria';
            color = 'brown darken-1';
            contrato = detalle.escalatoria.contrato;
          }

          if (detalle.gasto) {
            tipo = 'Gasto';
            color = 'pink darken-1';
            contrato = detalle.gasto.contrato;
          }
          return {
            ...detalle,
            contrato,
            tipo,
            color,
          };
        });
    },
  },
  watch: {
    'pago.idContratista': {
      handler(contratista) {
        if (!contratista) {
          this.pago.importe = 0;
          this.pago.detalles = [];
        }
      },
    },
    'pago.detalles': {
      handler(detalles) {
        this.pago.importe = this.$ObjSumar(detalles, 'importe');
      },
    },
  },
};
</script>

<style lang="scss">

</style>
